/**
 * Motivo estruturado de falha de um pedido de rota.
 *
 * Serve dois fins distintos:
 *  - diagnóstico interno (código estável, sem dados sensíveis);
 *  - mensagem clara ao utilizador, nunca um erro genérico.
 */

export type RequestFailureReason =
  | "place_not_found"
  | "outside_locality"
  | "nearby_locality"
  | "closed_in_window"
  | "insufficient_time"
  | "hours_unavailable"
  | "needs_locality_choice"
  | "technical_failure";

export const REQUEST_FAILURE_REASONS: readonly RequestFailureReason[] = [
  "place_not_found",
  "outside_locality",
  "nearby_locality",
  "closed_in_window",
  "insufficient_time",
  "hours_unavailable",
  "needs_locality_choice",
  "technical_failure",
] as const;

export function isRequestFailureReason(value: unknown): value is RequestFailureReason {
  return typeof value === "string" && (REQUEST_FAILURE_REASONS as readonly string[]).includes(value);
}

type MessageContext = {
  placeName?: string;
  area?: string;
  window?: string;
  /** Localidade real do local encontrado (ex.: "Perafita"). */
  placeLocality?: string;
};

/**
 * Grupo do motivo: nunca misturar "fora da zona" com "não cabe no horário".
 */
export type RequestFailureGroup = "schedule" | "area" | "not_found" | "technical";

export function failureReasonGroup(reason: RequestFailureReason): RequestFailureGroup {
  switch (reason) {
    case "closed_in_window":
    case "insufficient_time":
    case "hours_unavailable":
      return "schedule";
    case "outside_locality":
    case "nearby_locality":
    case "needs_locality_choice":
      return "area";
    case "place_not_found":
      return "not_found";
    default:
      return "technical";
  }
}

const TEMPLATES: Record<string, Record<RequestFailureReason, (c: MessageContext) => string>> = {
  pt: {
    place_not_found: (c) => `Não encontrámos ${c.placeName ?? "o local pedido"} em ${c.area ?? "essa zona"}.`,
    outside_locality: (c) => `${c.placeName ?? "O local pedido"} existe, mas fora de ${c.area ?? "essa localidade"}.`,
    nearby_locality: (c) =>
      `${c.placeName ?? "O local pedido"} fica ${c.placeLocality ? `em ${c.placeLocality}, ` : ""}perto de ${c.area ?? "essa localidade"} — não é na localidade exata que pediste.`,
    closed_in_window: (c) =>
      `${c.placeName ?? "O local pedido"} está fechado${c.window ? ` entre ${c.window}` : ""} nos horários habituais devolvidos pelo Google Places.`,
    insufficient_time: (c) =>
      `${c.placeName ?? "O local pedido"} só está aberto durante parte${c.window ? ` de ${c.window}` : ""} — não dá tempo para a visita.`,
    needs_locality_choice: (c) =>
      `Há mais do que um sítio possível para ${c.area ?? "essa localidade"}. Escolhe qual queres.`,
    hours_unavailable: (c) => `Não há horários habituais conhecidos para ${c.placeName ?? "o local pedido"}.`,
    technical_failure: () => "Houve uma falha técnica ao verificar os horários. Tenta novamente.",
  },
  en: {
    place_not_found: (c) => `We couldn't find ${c.placeName ?? "the requested place"} in ${c.area ?? "that area"}.`,
    outside_locality: (c) => `${c.placeName ?? "The requested place"} exists, but outside ${c.area ?? "that locality"}.`,
    nearby_locality: (c) =>
      `${c.placeName ?? "The requested place"} is ${c.placeLocality ? `in ${c.placeLocality}, ` : ""}near ${c.area ?? "that locality"} — not inside the exact locality you asked for.`,
    closed_in_window: (c) =>
      `${c.placeName ?? "The requested place"} is closed${c.window ? ` between ${c.window}` : ""} according to the usual hours returned by Google Places.`,
    insufficient_time: (c) =>
      `${c.placeName ?? "The requested place"} is only open for part${c.window ? ` of ${c.window}` : ""} — not enough time to visit.`,
    needs_locality_choice: (c) =>
      `There is more than one possible place for ${c.area ?? "that locality"}. Please choose which one you mean.`,
    hours_unavailable: (c) => `No usual opening hours are known for ${c.placeName ?? "the requested place"}.`,
    technical_failure: () => "A technical problem stopped the opening-hours check. Please try again.",
  },
};

export function failureReasonMessage(
  reason: RequestFailureReason,
  language: string,
  context: MessageContext = {},
): string {
  const lang = (language || "pt").slice(0, 2).toLowerCase();
  const table = TEMPLATES[lang] ?? TEMPLATES.en!;
  return table[reason](context);
}

/**
 * Registo interno (sem PII, sem stack trace, sem morada) para deixar de
 * existir apenas a mensagem genérica nos logs.
 */
export function logRequestFailure(
  scope: string,
  reason: RequestFailureReason,
  meta: Record<string, string | number | boolean | null> = {},
): void {
  console.info("routify.request_failure", { scope, reason, ...meta });
}
